import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { request, BaseResponse } from "../../../lib/api";
import { Toast, Rate } from "antd-mobile";
import { useI18n } from "../../../hooks/useI18n";
import { TimeUtils, formatTime, formatDuration, TIME_FORMATS } from "../../../utils/timeUtils";

// 导入图片资源
import bg2 from "@/assets/images/ai/bg2.png";
import ytg from "@/assets/images/ai/ytg.png";
import wtg from "@/assets/images/ai/wtg.png";
import logo3 from "@/assets/images/ai/logo3.png";
import a1 from "@/assets/images/ai/a1.png";
import a2 from "@/assets/images/ai/a2.png";
import err1 from "@/assets/images/ai/err1.png";
import ph from "@/assets/images/ai/ph.png";

// 组件导入
import ScoreRadar from "./components/ScoreRadar";
import LoadingSpinner from "@/pages/ai-training/analysis/components/LoadingSpinner";
import DimensionModal from "@/pages/ai-training/analysis/components/DimensionModal";
import GenerateAnalyze from "@/components/Loaders/GenerateAnalyze";
import { spawn } from "child_process";

interface TrainDetail {
  passed_status: "passed" | "failed";
  score: number;
  used_time: number;
  begin_time: string;
  dept_user_name: string;
}

interface UserInfo {
  target_title: string;
  rank?: number;
  avatar?: string;
  avatar_color: string;
  dept_user_name: string;
}

interface TotalInfo {
  label: string;
  score: number;
  total: number;
  comment: string;
}

interface AnswerContent {
  ai: {
    title: string;
  };
  user: {
    title: string;
    score: number;
    suggestion: string;
    gold_medal: string;
  };
}

interface ScoringCriteria {
  name: string;
  value: string;
}

// API响应数据类型
interface ApiResponse {
  success: boolean;
  data: any;
}

const AiTrainingAnalysis: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [isLoading, setIsLoading] = useState(true);
  const [selectLevel, setSelectLevel] = useState(1);
  const [showDimensionDesc, setShowDimensionDesc] = useState(false);
  const [currentLoadingText, setCurrentLoadingText] =
    useState(t('ai.analysis.creatingTask', '正在建立分析任务'));
  const [apiData, setApiData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionStatus, setSessionStatus] = useState<number | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);

  // 调用完成接口
  const callCompleteSession = async () => {
    if (!sessionId) return;

    try {
      await request(`/conversations/sessions/${sessionId}/complete`, {
        method: "POST",
        data: {
          completion_type: "normal",
        },
      });
      // 调用完成接口后，开始轮询获取状态
      setIsPolling(true);
      setCurrentLoadingText(t('ai.analysis.generatingReport', '生成分析报告中...'));
    } catch (error) {
      console.error("Error calling complete session:", error);
      Toast.show({ content: t('ai.analysis.startAnalysisFailed', '启动评价分析失败'), icon: "fail" });
    }
  };

  // API调用函数
  const fetchSessionDetail = async () => {
    if (!sessionId) {
      setError(t('ai.analysis.sessionIdNotFound', '未找到会话ID'));
      setIsLoading(false);
      return;
    }

    try {
      const response = await request<any>(
        `/conversations/sessions/${sessionId}/detail`
      );

      if (response.success && response.data) {
        const status = response.data.status;
        setSessionStatus(status);

        // 状态处理逻辑
        if (status === 0 || status === 3 || status === 4) {
          // 状态为0(对话中)、3(评分失败)或4(对话超时结束)，需要调用完成接口
          setCurrentLoadingText(
            status === 0
              ? t('ai.analysis.dialogEndedStartAnalysis', '对话已结束，正在启动评价分析')
              : status === 3
              ? t('ai.analysis.scoringFailedRestart', '评分失败，正在重新启动评价分析')
              : t('ai.analysis.timeoutStartAnalysis', '对话超时结束，正在启动评价分析')
          );
          await callCompleteSession();
          return; // 调用完成接口后会开始轮询，不设置数据
        } else if (status === 1 || status === 5) {
          // 状态为1(评分中)或5(评价中)，开始轮询
          setIsPolling(true);
          setCurrentLoadingText(
            status === 1 
              ? t('ai.analysis.analyzingPleaseWait', '正在进行评价分析，请稍候') 
              : t('ai.analysis.generatingComprehensiveReport', '正在生成综合评价报告，请稍候')
          );
          return; // 开始轮询，不设置数据
        } else if (status === 2) {
          // 状态为2(已完成)，正常显示数据
          setApiData(response.data);
          setIsLoading(false);
        } else {
          throw new Error(`Unknown session status: ${status}`);
        }
      } else {
        throw new Error("Failed to fetch session detail");
      }
    } catch (error) {
      console.error("Error fetching session detail:", error);
      setError(t('ai.analysis.fetchSessionDetailFailed', '获取分析详情失败'));
      Toast.show({ content: t('ai.analysis.fetchAnalysisDataFailed', '获取分析数据失败'), icon: "fail" });
      setIsLoading(false);
    }
  };

  // 从API数据中提取训练详情
  const getTrainDetail = (): TrainDetail => {
    if (!apiData)
      return {
        passed_status: "failed",
        score: 0,
        used_time: 0,
        begin_time: "",
        dept_user_name: "",
      };

    const score = apiData.session_scores?.overall_score || 0;
    const duration = apiData.duration || 0;

    return {
      passed_status: score >= (apiData.scenario_data?.passing_score || 60) ? "passed" : "failed",
      score: score,
      used_time: Math.abs(duration), // 使用绝对值处理负数时长
      begin_time: apiData.start_time || "",
      dept_user_name: apiData.user_name || "",
    };
  };

  // 从API数据中提取用户信息
  const getUserInfo = (): UserInfo => {
    if (!apiData)
      return { target_title: "", avatar_color: "#7461ee", dept_user_name: "" };

    return {
      target_title: apiData.scenario_data?.name || t('ai.analysis.defaultTitle', 'AI训练分析'),
      avatar: apiData.avatar,
      avatar_color: "#7461ee",
      dept_user_name: apiData.user_name || "",
    };
  };

  // 从API数据中提取总结
  const getSummarize = (): string => {
    return apiData?.session_scores?.feedback || t('ai.analysis.noSummary', '暂无分析总结');
  };

  // 从API数据中提取维度得分信息
  const getTotalInfo = (): TotalInfo[] => {
    if (!apiData?.session_scores?.dimension_scores) return [];

    const dimensionScores = apiData.session_scores.dimension_scores as Record<string, any>;
    const dimensionsOrder: string[] = (
      apiData?.scenario_data?.scoring_criteria_config?.criteria_data?.dimensions || []
    ).map((dim: any) => dim?.name).filter(Boolean);

    const orderMap = new Map<string, number>(
      dimensionsOrder.map((name: string, idx: number) => [name, idx])
    );

    const entries = Object.entries(dimensionScores) as [string, any][];

    entries.sort((a, b) => {
      const ai = orderMap.has(a[0]) ? (orderMap.get(a[0]) as number) : Number.MAX_SAFE_INTEGER;
      const bi = orderMap.has(b[0]) ? (orderMap.get(b[0]) as number) : Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });

    return entries.map(([key, value]) => ({
      label: key,
      score: value?.score ?? 0,
      total: value?.total ?? 100,
      comment: value?.feedback ?? t('ai.analysis.noComment', '暂无评价'),
    }));
  };

  // 从API数据中提取对话内容
  const getAnswerContent = (): AnswerContent[] => {
    if (!apiData?.messages) return [];

    return apiData.messages.map((group: any, index: number) => ({
      ai: {
        title: group.ai_message?.content || "",
      },
      user: {
        title: group.user_message?.content || "",
        score: group.user_message?.scores?.user_message_analysis?.score || 0,
        suggestion:
          group.user_message?.scores?.user_message_analysis?.suggestions ||
          t('ai.analysis.noSuggestion', '暂无建议'),
        gold_medal:
          group.user_message?.scores?.user_message_analysis
            ?.golden_responses?.[0] || t('ai.analysis.noGoldenResponse', '暂无推荐话术'),
      },
    }));
  };

  // 从API数据中提取评分标准
  const getScoringCriteria = (): ScoringCriteria[] => {
    if (
      !apiData?.scenario_data?.scoring_criteria_config?.criteria_data
        ?.dimensions
    )
      return [];

    const dimensions =
      apiData.scenario_data.scoring_criteria_config.criteria_data.dimensions;
    return dimensions.map((dim: any) => ({
      name: dim.name,
      value: dim.scoring_rule || t('ai.analysis.noDescription', '暂无描述'),
    }));
  };

  // 再试一次
  const tryAgain = () => {
    navigate(`/ai-training/detail/${apiData.scenario_data.id}`);
  };
  // 历史记录
  const toHistory = () => {
    navigate(`/ai-training/history/${apiData.scenario_data.id}`);
  };
  const toRankList = () => {
    navigate(`/ai-training/rank/list/${apiData.scenario_data.id}`);
  };

  // 计算动态数据
  const trainDetail = getTrainDetail();
  const userInfo = getUserInfo();
  const summarize = getSummarize();
  const totalInfo = getTotalInfo();
  const answerContent = getAnswerContent();
  const scoringCriteria = getScoringCriteria();

  // 加载文案切换
  const loadingTexts = [
    { text: t('ai.analysis.creatingTask', '正在建立分析任务'), minTime: 0, maxTime: 3 },
    { text: t('ai.analysis.正在进行维度评分', '正在进行维度评分'), minTime: 4, maxTime: 8 },
    { text: t('ai.analysis.正在生成维度评语', '正在生成维度评语'), minTime: 9, maxTime: 14 },
    {
      text: t('ai.analysis.分析已完成，正在生成您的专属综合报告', '分析已完成，正在生成您的专属综合报告'),
      minTime: 15,
      maxTime: Infinity,
    },
  ];

  useEffect(() => {
    // 调用API获取数据
    fetchSessionDetail();

    // 加载文案切换 - 只在加载状态下执行
    const textTimer = setInterval(() => {
      if (!isPolling && isLoading) {
        const randomIndex = Math.floor(Math.random() * loadingTexts.length);
        setCurrentLoadingText(loadingTexts[randomIndex].text);
      }
    }, 2000);

    return () => {
      clearInterval(textTimer);
    };
  }, [sessionId, isPolling, isLoading]);

  // 轮询逻辑
  useEffect(() => {
    let pollingTimer: NodeJS.Timeout;

    if (isPolling) {
      pollingTimer = setInterval(async () => {
        try {
          const response = await request<any>(
            `/conversations/sessions/${sessionId}/detail`
          );

          if (response.success && response.data) {
            const status = response.data.status;
            setSessionStatus(status);

            if (status === 2) {
              // 评分完成，停止轮询并显示数据
              setIsPolling(false);
              setApiData(response.data);
              setIsLoading(false);
              setCurrentLoadingText(t('ai.analysis.evaluationComplete', '评价分析已完成'));
            } else if (status === 3) {
              // 评分失败，停止轮询并显示错误
              setIsPolling(false);
              setError(t('ai.analysis.analysisFailedRetry', '评价分析失败，请重试'));
              setIsLoading(false);
            }
            // 状态为1时继续轮询
          }
        } catch (error) {
          console.error("Polling error:", error);
          setIsPolling(false);
          setError(t('ai.analysis.fetchEvaluationStatusFailed', '获取评价状态失败'));
          setIsLoading(false);
        }
      }, 2500); // 每秒轮询一次
    }

    return () => {
      if (pollingTimer) {
        clearInterval(pollingTimer);
      }
    };
  }, [isPolling, sessionId]);

  // 错误处理渲染
  if (error) {
    return (
      <div
        className='min-h-screen p-5 bg-cover bg-no-repeat flex items-center justify-center'
        style={{ backgroundImage: `url(${bg2})` }}
      >
        <div className='text-center'>
          <div className='text-red-500 text-lg mb-4'>{t('ai.analysis.loadFailed', '加载失败')}</div>
          <div className='text-gray-600 text-sm'>{error}</div>
          <button
            onClick={() => {
              setError(null);
              setIsLoading(true);
              fetchSessionDetail();
            }}
            className='mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
          >
            {t('common.retry', '重试')}
          </button>
        </div>
      </div>
    );
  }

  // 格式化时间
  const getUsedTime = (seconds: number) => {
    return formatDuration(seconds);
  };

  // 格式化日期时间
  const formatDateTime = (dateTimeStr: string) => {
    if (!dateTimeStr) return "";
    // 假设后端返回的是UTC时间，转换为本地时间显示
    return formatTime(dateTimeStr, TIME_FORMATS.DATETIME_MINUTE, true);
  };

  // 根据分数获取星星
  const getStarsByScore = (score: number) => {
    const fullStars = Math.floor(score / 20);
    const hasHalfStar = score % 20 >= 10;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    const stars = [];
    for (let i = 0; i < fullStars; i++) stars.push("full");
    if (hasHalfStar) stars.push("half");
    for (let i = 0; i < emptyStars; i++) stars.push("empty");

    return { stars };
  };

  if (isLoading) {
    return (
      <div
        className='min-h-screen p-5 bg-cover bg-no-repeat'
        style={{ backgroundImage: `url(${bg2})` }}
      >
        <div className='mt-52 flex items-center flex-col'>
          <GenerateAnalyze />
          <div className='text-[#303133] text-sm leading-[22px] text-center font-normal mt-6'>
            <span className='bg-gradient-to-r from-[#5148ff] to-[#b02cfb] bg-clip-text text-transparent'>
              {currentLoadingText}
            </span>
            {isPolling && (
              <div className='mt-2 text-xs text-[#909399]'>
                {t('ai.analysis.pollingStatusText', '正在轮询获取评价状态...')}
              </div>
            )}
            {sessionStatus !== null&&false && (
              <div className='mt-2 text-xs text-[#909399]'>
                {t('当前状态')}:{" "}
                {sessionStatus === 0
                  ? t('ai.status.chatting', '对话中')
                  : sessionStatus === 1
                  ? t('ai.status.scoring', '评分中')
                  : sessionStatus === 2
                  ? t('ai.status.completed', '已完成')
                  : sessionStatus === 3
                  ? t('ai.status.scoringFailed', '评分失败')
                  : sessionStatus === 4
                  ? t('ai.status.timeout', '对话超时结束')
                  : t('ai.status.unknown', '未知状态')}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 超时状态渲染
  if (isTimeout) {
    return (
      <div
        className='min-h-screen p-5 bg-cover bg-no-repeat'
        style={{ backgroundImage: `url(${bg2})` }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className='mt-3 pb-28'
        >
          {/* 标题 */}
          <div className='text-[#303133] text-lg leading-6 text-left font-medium mb-4'>
            {userInfo.target_title || t('ai.analysis.defaultTitle', 'AI训练分析')}
          </div>

          {/* 超时提示卡片 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className='relative flex flex-col items-center justify-center p-8 mb-5 rounded-2xl min-h-[300px] bg-white'
            style={{
              boxShadow: "0px 0px 28px -2px rgba(53, 14, 145, 0.08)",
            }}
          >
            {/* 超时图标 */}
              <div className='mb-6'>
                <img className='w-20 h-20' src={err1} alt={t('ai.status.timeout', '超时')} />
              </div>

            {/* 超时提示文字 */}
            <div className='text-center mb-6'>
              <div className='text-[#303133] text-xl leading-7 font-medium mb-2'>
                  {t('ai.status.timeout', '对话超时结束')}
              </div>
              <div className='text-[#909399] text-sm leading-6'>
                  {t('ai.analysis.unableToAnalyzeDueToDuration', '由于对话时间过长，系统无法生成评价分析')}
              </div>
              <div className='text-[#909399] text-sm leading-6'>
                  {t('ai.analysis.pleaseRestartOrViewHistory', '请重新开始训练或查看历史记录')}
              </div>
            </div>

            {/* 操作按钮 */}
            <div className='flex gap-4'>
              <button
                onClick={tryAgain}
                className='px-6 py-3 bg-gradient-to-r from-[#5148ff] to-[#b02cfb] text-white rounded-xl text-sm font-medium hover:opacity-90 transition-opacity'
              >
                {t('ai.analysis.retryTraining', '重新训练')}
              </button>
              <button
                onClick={toHistory}
                className='px-6 py-3 bg-[#f5f5f5] text-[#606266] rounded-xl text-sm font-medium hover:bg-[#e8e8e8] transition-colors'
              >
                {t('ai.analysis.viewHistory', '查看历史')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className='min-h-screen p-5 bg-cover bg-no-repeat'
      style={{ backgroundImage: `url(${bg2})` }}
    >
      {/* 主体内容容器 - 添加最大宽度和居中 */}
      <div className='max-w-4xl mx-auto'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className='mt-3 pb-28'
        >
        {/* 标题 */}
        <div className='text-[#303133] text-lg leading-6 text-left font-medium mb-4'>
            {userInfo.target_title}
          </div>

        {/* 用户信息卡片 */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className='relative flex items-center p-3 mb-1 rounded-2xl min-h-[126px]'
          style={{
            background:
              "linear-gradient(171deg, #a782ff 0%, #828eff 100%), linear-gradient(90deg, #82cbff 0%, #a782ff 100%), rgba(255, 255, 255, 0.55)",
            boxShadow: "0px 0px 28px -2px rgba(53, 14, 145, 0.08)",
          }}
        >
          {/* 成绩图标 */}
          <div className='absolute top-0 right-3 -mt-3 flex items-center justify-center'>
            <img
              className='w-20 h-20 rounded-3xl'
              src={trainDetail.passed_status === "passed" ? ytg : wtg}
              alt=''
            />
            <div
              className={`absolute top-5 left-0 w-20 text-xl flex items-end justify-center ${
                trainDetail.passed_status === "passed"
                  ? "text-[#F9B43D]"
                  : "text-[#989CA3]"
              }`}
            >
              {trainDetail.score}
            </div>
            <div
              className={`absolute top-11 left-0 w-20 text-xs flex items-end justify-center ${
                trainDetail.passed_status === "passed"
                  ? "text-white"
                  : "text-[#767779]"
              }`}
            >
              {trainDetail.passed_status === "passed" ? t('ai.analysis.passed', '已通过') : t('ai.analysis.failed', '未通过')}
            </div>
          </div>

          {/* 排名标签 */}
          {userInfo.rank && (
            <div className='absolute top-0 left-0 bg-[#c7d3ff] text-[#5263ff] py-1 px-4 text-sm leading-[22px] font-normal rounded-tl-2xl rounded-br-2xl'>
              {t('ai.analysis.rankLabel', '第{rank}名').replace('{rank}', String(userInfo.rank))}
            </div>
          )}

          {/* 用户信息 */}
          <div className='flex items-center pt-6'>
            {/* 头像 */}
            <div>
              {userInfo.avatar ? (
                <img
                  className='w-[52px] h-[52px] rounded-full'
                  src={userInfo.avatar}
                  alt=''
                />
              ) : (
                <span
                  className='inline-block text-sm w-[52px] h-[52px] rounded-full leading-[52px] text-center text-white'
                  style={{ background: userInfo.avatar_color }}
                >
                  {userInfo.dept_user_name.slice(-2)}
                </span>
              )}
            </div>
            <div className='text-left ml-3'>
              <div className='text-white text-sm leading-[22px] font-normal mt-1'>
                {t('ai.analysis.name', '姓名')}：{trainDetail.dept_user_name}
              </div>
              <div className='text-white text-sm leading-[22px] font-normal mt-1'>
                {t('ai.analysis.conversationDuration', '对话时长')}：{getUsedTime(trainDetail.used_time)}
              </div>
              <div className='text-white text-sm leading-[22px] font-normal mt-1'>
                {t('ai.analysis.participationTime', '参与时间')}：{formatDateTime(trainDetail.begin_time)}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tab切换 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
          className='mt-5'
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3, delay: 0.6 }}
            className='relative bg-[#eeebf4] rounded-2xl p-1 mb-5 flex items-center justify-around pb-2.5'
          >
            <div
              className={`relative w-1/2 text-center transition-all duration-300 z-10 text-[#303133] text-base leading-6 font-medium p-2 cursor-pointer ${
                selectLevel === 1
                  ? "text-white bg-[#a782ff] rounded-xl"
                  : "text-[#606266]"
              }`}
              onClick={() => setSelectLevel(1)}
            >
              {t('ai.analysis.overallPerformance', '整体表现')}
            </div>
            <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-[20deg] w-0.5 h-4 bg-[#d8d8d8]'></div>
            <div
              className={`relative w-1/2 text-center transition-all duration-300 z-10 text-[#303133] text-base leading-6 font-medium p-2 cursor-pointer ${
                selectLevel === 2
                  ? "text-white bg-[#a782ff] rounded-xl"
                  : "text-[#606266]"
              }`}
              onClick={() => setSelectLevel(2)}
            >
              {t('ai.analysis.conversationAnalysis', '对话分析')}
            </div>
          </motion.div>

          {/* 内容区域 */}
          <AnimatePresence mode='wait'>
            {selectLevel === 1 ? (
              <motion.div
                key='overall'
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                className='p-3 bg-white rounded-2xl mt-5'
                style={{
                  boxShadow: "0px 0px 28px -2px rgba(53, 14, 145, 0.08)",
                }}
              >
                {/* 各维度得分 */}
                <div className='flex items-center justify-between mt-3'>
                  <div className='flex items-center'>
                    <img className='w-[18px] h-[18px]' src={logo3} alt='' />
                    <span className='text-[#303133] text-base leading-6 font-medium ml-2'>
                      {t('ai.analysis.dimensionScores', '各维度得分')}
                    </span>
                  </div>
                  <div
                    className='text-[#7461ee] text-sm leading-[22px] font-medium cursor-pointer'
                    onClick={() => setShowDimensionDesc(true)}
                  >
                    {/* {t('ai.analysis.dimensionDescription', '维度说明')} */}
                  </div>
                </div>
                <div>
                  <ScoreRadar totalInfo={totalInfo} className='mt-3' />
                </div>

                {/* 总结 */}
                <div className='mt-3'>
                  <div className='flex items-center'>
                    <img className='w-[18px] h-[18px]' src={logo3} alt='' />
                    <span className='text-[#303133] text-base leading-6 font-medium ml-2'>
                      {t('ai.analysis.summary', '总结')}
                    </span>
                  </div>
                  <div className='text-[#606266] text-sm leading-[22px] font-normal text-left mt-3'>
                    {summarize}
                  </div>
                </div>

                {/* 各项数据 */}
                {totalInfo.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className='mt-5'
                  >
                    <div className='flex items-center'>
                      <img className='w-[18px] h-[18px]' src={logo3} alt='' />
                      <span className='text-[#303133] text-base leading-6 font-medium ml-2'>
                        {item.label}
                        <span className='text-[#7a63e9] ml-1'>
                          ({item.score}/{<span className="text-[#00000090]">{item.total}</span>})
                        </span>
                      </span>
                    </div>
                    <div className='text-[#606266] text-sm leading-[22px] font-normal text-left mt-3'>
                      {item.comment}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key='dialogue'
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {answerContent.length > 0 ? (
                  answerContent.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className='mt-5 bg-white p-3 rounded-2xl'
                      style={{
                        boxShadow: "0px 0px 28px -2px rgba(53, 14, 145, 0.08)",
                      }}
                    >
                      {/* 问题 */}
                      <div className='flex mb-4'>
                        <div className='text-left'>
                          <div className='text-[#606266] text-sm leading-[22px] font-normal'>
                            <span className='text-[#A782FF] text-sm leading-[22px]'>
                              {index + 1}.
                            </span>
                            <span className='text-[#303133] text-sm leading-[22px] font-medium ml-1'>
                              {t('ai.analysis.aiLabel', 'AI：')}
                            </span>
                            {item.ai.title}
                          </div>
                          {/* 回答 */}
                          {item.user.title && (
                            <>
                              <div className='text-sm mt-2 leading-[22px] font-normal'>
                                <span className='text-[#303133] font-medium'>
                                {t('ai.analysis.studentLabel', '学员：')}
                                </span>
                                <span className='text-[#606266]'>
                                  {item.user.title}
                                </span>
                              </div>
                              <div className='relative mt-2 pl-[34px] pr-4  text-[#A782FF] text-sm leading-[22px] rounded-full flex items-center'>
                                <img
                                  className='absolute -top-0.5 -left-0.5 w-6 h-6'
                                  src={
                                    item.user.score >= 80
                                      ? a2
                                      : item.user.score < 60
                                      ? err1
                                      : a1
                                  }
                                  alt=''
                                />
                                {/* 星星评分 */}
                                <Rate
                                   value={item.user.score / 20}
                                   allowHalf
                                   count={5}
                                   readOnly
                                   style={{ '--star-size': '16px', color: '#A782FF' }}
                                 />
                                <span className='ml-1 text-[#A782FF] text-sm font-medium'>
                                  {/* {item.user.score}分 */}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      {/* 改进建议 */}
                      {item.user.title && (
                        <div className='text-left'>
                          <div className='text-[#303133] text-sm leading-[22px] font-medium'>
                            {t('ai.analysis.improvementSuggestions', '改进建议')}
                          </div>
                          <div className='text-sm leading-[22px] font-normal mt-2'>
                            <span className='text-[#303133]'>{t('ai.analysis.answer', '回答')}：</span>
                            <span className='text-[#606266]'>
                              {item.user.suggestion}
                            </span>
                          </div>
                          <div className='text-[#303133] text-sm leading-[22px] font-medium mt-3'>
                            {t('ai.analysis.goldenResponseRecommendation', '金牌话术推荐')}
                          </div>
                          <div className='text-sm leading-[22px] font-normal mt-2'>
                            <span className='text-[#303133]'>{t('ai.analysis.answer', '回答')}：</span>
                            <span className='text-[#606266]'>
                              {item.user.gold_medal}
                            </span>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))
                ) : (
                  <div className='flex items-center flex-col mt-20'>
                    <LoadingSpinner />
                    <div className='text-sm leading-4 font-normal text-center mt-5 bg-gradient-to-r from-[#5148ff] to-[#b02cfb] bg-clip-text text-transparent'>
                      {t('ai.analysis.generatingGoldenResponses', '金牌话术正在精心提炼中')}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {answerContent.length > 0 && (
          <div className='text-[#909399] text-xs leading-4 font-normal text-center mt-5'>
            {t('ai.analysis.aiGeneratedDisclaimer', '----内容由AI生成，仅供参考----')}
          </div>
        )}
      </motion.div>

      {/* 底部操作栏 */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className='fixed left-0 bottom-0 w-full flex items-center justify-center pb-4 pt-4 bg-white'
        style={{ boxShadow: "0px -14px 22px 0px rgba(0, 0, 0, 0.06)" }}
      >
        <div className='flex items-center justify-between px-5 w-full max-w-4xl'>
          <div
            onClick={toRankList}
            className='rounded-[39px] cursor-pointer'
            style={{
              background: "rgba(255, 255, 255, 0.55)",
              boxShadow: "0px 0px 28px -2px rgba(53, 14, 145, 0.08)",
            }}
          >
            <img style={{ width: "55px", height: "50px" }} src={ph} alt='' />
          </div>
          <div className='flex items-center'>
            <div
              onClick={toHistory}
              className='rounded-[39px] text-[#7461ee] text-sm mr-2 leading-[22px] text-center py-3 cursor-pointer'
              style={{
                width: "136px",
                background: "rgba(255, 255, 255, 0.55)",
                boxShadow: "0px 0px 28px -2px rgba(53, 14, 145, 0.08)",
                border: "2px solid #7461ee",
              }}
            >
{t('ai.analysis.practiceHistory', '对练记录')}
            </div>
            <div
              onClick={tryAgain}
              className='rounded-[39px] text-white text-sm leading-[22px] text-center py-3 cursor-pointer'
              style={{
                width: "136px",
                background:
                  "linear-gradient(171deg, #a782ff 0%, #828eff 100%), linear-gradient(90deg, #82cbff 0%, #a782ff 100%), rgba(255, 255, 255, 0.55)",
                boxShadow: "0px 0px 28px -2px rgba(31, 6, 90, 0.08)",
              }}
            >
{t('ai.analysis.practiceAgain', '再练一次')}
            </div>
          </div>
        </div>
      </motion.div>
      </div>

      {/* 维度说明弹窗 */}
      <DimensionModal
        visible={showDimensionDesc}
        onClose={() => setShowDimensionDesc(false)}
        dimensions={scoringCriteria}
      />
    </div>
  );
};

export default AiTrainingAnalysis;
